import { FC, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { TermsAndConditions } from '@utils';

interface TermsModalProps {
  open: boolean;
  onClose: () => void;
  type: 'terms' | 'privacy';
}

const TermsModal: FC<TermsModalProps> = memo(({ open, onClose, type }) => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';

  if (!open) return null;

  const title = type === 'terms' 
    ? (isRtl ? 'شروط الخدمة' : 'Terms of Service')
    : (isRtl ? 'سياسة الخصوصية' : 'Privacy Policy');

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.48)',
          backdropFilter: 'blur(2.6px)',
          zIndex: 1000,
        }}
        onClick={onClose}
      />
      {/* Modal */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'white',
          borderRadius: '24px',
          width: '720px',
          maxWidth: '90vw',
          maxHeight: '80vh',
          boxShadow: '2px 4px 20px 0px rgba(0, 0, 0, 0.1)',
          zIndex: 1001,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          direction: isRtl ? 'rtl' : 'ltr',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 24px',
            height: '81px',
            borderBottom: '1px solid #e5e7eb',
            flexShrink: 0,
          }}
        >
          {/* Close button */}
          <button
            onClick={onClose}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
            }}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 5L5 15M5 5L15 15" stroke="#666666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          {/* Title */}
          <h2
            style={{
              fontFamily: "'IBM Plex Sans Arabic', sans-serif",
              fontSize: '20px',
              fontWeight: 700,
              lineHeight: '28px',
              color: '#262626',
              margin: 0,
            }}
          >
            {title}
          </h2>
        </div>

        {/* Scrollable Content */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            textAlign: isRtl ? 'right' : 'left',
          }}
        >
          {/* Intro text */}
          <p
            style={{
              fontFamily: "'IBM Plex Sans Arabic', sans-serif",
              fontSize: '16px',
              fontWeight: 400,
              lineHeight: '24px',
              color: '#262626',
              margin: 0,
            }}
          >
            {isRtl 
              ? 'مرحبًا بك في منصة Msool لإدارة العقارات. باستخدامك لهذه المنصة، فإنك توافق على الالتزام بالشروط والأحكام التالية. يُرجى قراءة هذه الشروط بعناية قبل استخدام الخدمة.'
              : 'Welcome to the Msool property management platform. By using this platform, you agree to comply with the following terms and conditions. Please read these terms carefully before using the service.'
            }
          </p>

          {/* Terms sections */}
          {TermsAndConditions.map((item, index) => (
            <div
              key={item.id}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
              }}
            >
              {/* Section title */}
              <h3
                style={{
                  fontFamily: "'IBM Plex Sans Arabic', sans-serif",
                  fontSize: '18px',
                  fontWeight: 600,
                  lineHeight: '24px',
                  color: '#262626',
                  margin: 0,
                }}
              >
                {index + 1}. {t(item.questionKey)}
              </h3>
              {/* Section content */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                }}
              >
                {item.pointsKeys.map((pointKey, pointIndex) => (
                  <p
                    key={pointIndex}
                    style={{
                      fontFamily: "'IBM Plex Sans Arabic', sans-serif",
                      fontSize: '16px',
                      fontWeight: 400,
                      lineHeight: '24px',
                      color: '#262626',
                      margin: 0,
                    }}
                  >
                    {t(pointKey)}
                  </p>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            padding: '17px 24px 16px',
            borderTop: '1px solid #eaeaea',
            flexShrink: 0,
          }}
        >
          <button
            onClick={onClose}
            style={{
              width: '156px',
              height: '48px',
              borderRadius: '16px',
              border: '1px solid #5b2ff9',
              background: 'white',
              color: '#5b2ff9',
              fontFamily: "'IBM Plex Sans Arabic', sans-serif",
              fontSize: '17px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#f8f7ff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'white';
            }}
          >
            {isRtl ? 'إغلاق' : 'Close'}
          </button>
        </div>
      </div>
    </>
  );
});

TermsModal.displayName = 'TermsModal';

export default TermsModal;
